import { useMemo } from 'react';

import type { GetStylePicturesQueryVariables } from '@buying/library';

import { useQuery } from '@apollo/client/react';

import {
    findStyleOptionImage,
    GetStylePicturesDocument,
    GraphClientNames,
    isDefined,
} from '@buying/library';

interface GetStyleThumbnailPictureType {
    name: string;
    src: string;
    alt: string;
}

interface HookReturnType {
    picture: GetStyleThumbnailPictureType | undefined;
    isLoading: boolean;
}

const PlaceholderImage = {
    name: 'Style image missing',
    src: '',
    alt: 'Style image missing',
};

const useGetStyleThumbnailPicture = (
    styleNumber:
        | GetStylePicturesQueryVariables['styleNumber']
        | null
        | undefined
): HookReturnType => {
    // Fetch style images
    const {
        data: styleData,
        loading: isStyleLoading,
        error: styleError,
    } = useQuery(GetStylePicturesDocument, {
        context: {
            clientName: GraphClientNames['bestone-bi4-buying-style-bff'],
        },
        skip: !styleNumber,
        variables: {
            styleNumber: styleNumber ?? 0,
        },
    });

    // Derive pictures from styleData
    const styleThumbnailPicture = useMemo(() => {
        if (styleError || !styleData?.style) {
            return PlaceholderImage;
        }

        const imagesData =
            styleData.style.styleOptions
                ?.flatMap((option) => option?.pictures)
                .filter(isDefined) ?? [];

        const thumbnailImage = findStyleOptionImage(imagesData, 'thumbnail');

        if (!thumbnailImage?.url) {
            return PlaceholderImage;
        }

        return {
            name: thumbnailImage.perspectiveName ?? `Image for ${styleNumber}`,
            src: thumbnailImage.url,
            alt: thumbnailImage.perspectiveName ?? `Image for ${styleNumber}`,
        };
    }, [styleError, styleData, styleNumber]);

    return {
        picture: styleThumbnailPicture,
        isLoading: isStyleLoading,
    };
};

export { useGetStyleThumbnailPicture, PlaceholderImage };
